<template>
  <BaseModal
    v-if="shouldBeVisible"
    :heading="t('kyc_wall_modal.heading')"
    small
    unclosable
    testId="kyc-wall"
  >
    <EmptyPageComponent
      imageName="treatwell"
      :text="{
        description: t('kyc_wall_modal.content.description'),
        btnPrimary: t('kyc_wall_modal.content.button_primary'),
        btnSecondary: t('kyc_wall_modal.content.button_secondary')
      }"
      @click="handleEmptyPageAction"
      v-test="'kyc-wall-modal-content'"
    />
    <template #footer>
      <BaseText 
        role="button"
        link
        @click="navigateToBilling"
        v-test="'kyc-wall-modal-cancel'"
      >
        {{ t('kyc_wall_modal.cancel_subscription') }}
      </BaseText>
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { computed, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { useCompanyStore } from '@/stores/company';
import { useTreatwellStore } from '@/stores/treatwell';
import EmptyPageComponent from '@/components/EmptyPageContent.vue';
import { useLazyQuery } from '@vue/apollo-composable';
import dayjs from 'dayjs';
import gql from 'graphql-tag';
import { SubscriptionPlan, StripeSubscriptionStatus } from '@/types';

interface StripeSubscriptionFromQuery {
  id: string;
  periodStart: string;
  status: StripeSubscriptionStatus;
}

const { t } = useI18n();

const router = useRouter();
const route = useRoute();
const { company } = useCompanyStore();
const { treatwellStatus } = useTreatwellStore();

const GET_STRIPE_SUBSCRIPTIONS_QUERY = gql`
  query getStripeSubscriptions {
    stripeSubscriptions {
      id
      status
      periodStart
    }
  }
`;

const {
  load: fetchStripeSubscriptions,
  result: stripeSubscriptionsData,
  loading: isLoadingStripeSubscriptions,
} = useLazyQuery<{ stripeSubscriptions: StripeSubscriptionFromQuery[] }>(
  GET_STRIPE_SUBSCRIPTIONS_QUERY,
  null,
  { fetchPolicy: 'network-only' },
);
if (
  company.subscribed &&
  company.activeSubscription?.plan === SubscriptionPlan.Lite.toLowerCase()
) {
  fetchStripeSubscriptions();
}

const kycExclusionRouteNames: string[] = [
  'treatwell-landing',
  'treatwell-admin',
  'subscription',
  'stripe-checkout',
  'tw-checkout',
  'admin-billing',
  'admin-billing-legacy',
];

const isExcludedRoute = computed(() => {
  return kycExclusionRouteNames.some((routeName) =>
    route.matched.some((matchedRoute) => matchedRoute.name === routeName),
  );
});

const gracePeriodPassed = computed(() => {
  if (isLoadingStripeSubscriptions.value) {
    return false;
  }
  const subscriptions = stripeSubscriptionsData.value?.stripeSubscriptions;
  if (!subscriptions || subscriptions.length === 0) {
    return false;
  }
  const activeSubscription = subscriptions.find(
    (sub) => sub.status === StripeSubscriptionStatus.Active,
  );
  if (!activeSubscription || !activeSubscription.periodStart) {
    return false;
  }
  const gracePeriodCalculationStartDate = dayjs(
    activeSubscription.periodStart,
  ).startOf('day');
  const endOf7thDayOfGrace = gracePeriodCalculationStartDate
    .add(6, 'days')
    .endOf('day');
  const isAfterGracePeriodCompletely = dayjs().isAfter(endOf7thDayOfGrace);
  return isAfterGracePeriodCompletely;
});

const shouldBeVisible = computed(() => {
  if (isExcludedRoute.value) {
    return false;
  }

  const isLite =
    company.activeSubscription?.plan === SubscriptionPlan.Lite.toLowerCase();
  const kycIncomplete = treatwellStatus?.kycStatus?.kycCompleted === false;
  const gracePassed = gracePeriodPassed.value;

  return isLite && kycIncomplete && gracePassed;
});

function handleEmptyPageAction(actionType: 'primary' | 'secondary') {
  if (actionType === 'primary') {
    router.push({ name: 'treatwell-admin' });
  } else if (actionType === 'secondary') {
    router.push({ name: 'subscription' });
  }
}

function navigateToBilling() {
  router.push({ name: 'admin-billing' });
}
</script>
